import type { DataGridContextMenuProps } from "src/components/DataGrid/useDataGridRowContextMenu.tsx";
import { useMutation } from "@tanstack/react-query";
import { download } from "@exonn/utils";
import { getExonnApi } from "src/exonn-api/exonn-api.tsx";
import type { GridValidRowModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "src/App/App.hooks.ts";

import { ResourceEnum } from "src/constants/constants.ts";
import { useErpTranslation } from "src/system/useErpTranslation.ts";
import { type BankSepaResource, TextFilterModeEnum } from "@exonn/js-sdk";
import { useRouter } from "src/lib/react-router/useRouter.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import {
  ResourceListSearchParams,
  type UrlFilterModel,
  type UrlState,
} from "src/system/useResourceList/useResourceList.types.ts";
import qs from "qs";

export const useBankSepaListContextMenu = (): {
  items: DataGridContextMenuProps["items"];
  isPending: boolean;
} => {
  const { t } = useErpTranslation({
    resource: ResourceEnum.BankSepa,
    type: "layout",
  });
  const router = useRouter();
  const snackbar = useSnackbar();

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const api = getExonnApi();
      return await api.client.get(`/bank-sepa/${id}/download-xml`, {
        responseType: "blob",
      });
    },
  });
  return {
    isPending: isPending,
    items: [
      {
        key: "show-postings",
        label: t("contextActions.showPostings"),
        icon: <span className={"icon-20-reservation"} />,
        onSelect: (row: BankSepaResource) => {
          const paths = createModuleRoutePaths({
            resource: ResourceEnum.AccountingPosting,
          });
          const filterModel: UrlFilterModel = {
            ["id"]: {
              v: (row.accounting_posting_sepas || []).map(
                (res) => res.accounting_posting_id,
              ),
              o: TextFilterModeEnum.Exact,
            },
          };

          const obj: UrlState = {
            [ResourceListSearchParams.FilterModel]: filterModel,
          };

          return router.navigate(
            `${paths.root}?${qs.stringify(obj, {
              encode: false,
            })}`,
          );
        },
      },
      {
        key: "download",
        label: t("contextActions.download"),
        icon: <span className={"icon-20-arrow-small-down"} />,
        hide: (row: BankSepaResource) => {
          return !row.has_file;
        },
        onSelect: (row: GridValidRowModel) => {
          mutate(
            {
              id: row.id,
            },
            {
              onSuccess: (response) => {
                if (response.data) {
                  download(response.data, `${row.id}_sepa_pain.001.xml`);
                }
              },
              onError: () => {
                snackbar.error({
                  flash: true,
                  message: t("notifications.downloadXmlFile.error"),
                });
              },
            },
          );
        },
      },
    ],
  };
};
